import React, { useEffect, useState, useMemo } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MultiSelect } from 'react-multi-select-component';
import { CCol, CRow, CListGroup } from '@coreui/react';
import { RootState } from '../../store/state';
import { Job, JobPhase } from '../../shared/models/jobs';
import { StartJobsPoll, StopJobsPoll } from '../../jobs/actions';
import {
  selectJobs,
  selectLiveMapActiveJobs,
} from '../../store/selectors/job.selectors';
import { selectTechniciansIncludingRemoved } from '../../store/selectors/user.selectors';
import { UserInfo } from '../../shared/models/user';
import { LoadUsers } from '../../technicians/actions/userManagement.action';
import ActiveJobsMap from '../components/ActiveJobsMap';
import { selectOrgHierarchySelectedOrg } from '../../store/selectors/orgHierarchy.selectors';
import LiveMapActiveDrivers from '../components/LiveMapActiveDrivers';
import orgLocationIcon from '../../assets/images/logo-org-new.png';
import { selectJourneyConfig } from '../../store/selectors/journey.selectors';
import { selectSessions } from '../../store/selectors/session.selectors';
import { Session } from '../../shared/models/session';
import { StartSessionsPoll, StopSessionsPoll } from '../../jobs/actions/sessions';
import LiveMapActiveSessionDrivers from '../components/LiveMapActiveSessionDrivers';

export interface DashboardLiveMapProps extends DispatchProp {
  // eslint-disable-next-line react/no-unused-prop-types
  jobs: Job[];
  activeJobs: Job[];
  sessions: Session[];
  // eslint-disable-next-line react/no-unused-prop-types
  jobsLoading: boolean;
  technicians: UserInfo[];
  // eslint-disable-next-line react/no-unused-prop-types
  techniciansLoading: boolean;
}

const DashboardLiveMap = (props: DashboardLiveMapProps) => {
  const { activeJobs, technicians, dispatch, sessions } = props;
  const config = useSelector(selectJourneyConfig);
  const [selectedJob, setSelectedJob] = useState(null);
  const [invites, setInvites] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);
  const selectedOrgID = selectedOrg?._id;
  const {
    enableOrgHierarchyModule,
    liveMapFilter,
  } = useFlags();
  const isSessionsEnabled = selectedOrg?.config?.sessions?.enabled;
  const baseLocation = selectedOrg?.base_location;

  useEffect(() => {
    if (selectedOrgID) {
      if (isSessionsEnabled) {
        dispatch(new StartSessionsPoll(selectedOrgID, {
          from_ts: moment().startOf('day').subtract(3, 'days').unix()*1000,
          to_ts: moment().endOf('day').unix()*1000,
          children: enableOrgHierarchyModule,
          state: 'not_completed',
        }));
      } else {
        dispatch(new StartJobsPoll(selectedOrgID, {
          from_ts: moment().startOf('day').subtract(3, 'days').unix()*1000,
          to_ts: moment().endOf('day').unix()*1000,
          children: enableOrgHierarchyModule,
          state: 'not_completed',
        }));
      }
      dispatch(new LoadUsers({
        orgId: selectedOrgID,
        includeChildren: enableOrgHierarchyModule
      }));
    }
    return () => {
      dispatch(new StopJobsPoll());
      dispatch(new StopSessionsPoll());
    }
  }, [selectedOrgID]);

  const selectJobHandler = (job) => {
    if (selectedJob?.invite_code !== job.invite_code) {
      setSelectedJob(job);
    } else {
      setSelectedJob(null);
    }
  };

  const onInviteListChanged = (updatedInvites) => {
    setInvites(updatedInvites)
  };

  const storeOpts = useMemo(() => {
    return liveMapFilter !== '' ? activeJobs?.reduce((res, job) => {
      const storeMetadata = job?.metadata?.find(m => m.n === liveMapFilter);
      const isNew = !res?.find(opt => opt?.value === storeMetadata?.v);
      if (storeMetadata && isNew) {
        res.push({
          label: storeMetadata.v,
          value: storeMetadata.v,
        });
      }
      return res;
    }, []) : [];
  }, [activeJobs, liveMapFilter]);

  const onSelectStore = (value: React.SetStateAction<any[]>) => {
    setSelectedStores(value);
  };

  const getJobAgentName = (agentId: number) => {
    let name = 'Unassigned';
    if (agentId !== -1) {
      name = technicians?.find(tech => tech.id === agentId)?.displayName || 'Unassigned';
    }
    return name;
  }

  const filteredJobs = useMemo(() => {
    const filteredResults = selectedStores?.length > 0 ? activeJobs?.filter(job => {
      const storeMetadata = job?.metadata?.find(m => m?.n === liveMapFilter);
      return selectedStores?.find(s => s?.value === storeMetadata?.v);
    }) : activeJobs;
    const sortedResults = filteredResults?.sort((a, b) => {
      return getJobAgentName(a?.agent_id)?.localeCompare(getJobAgentName(b?.agent_id), 'en', { sensitivity: 'base' });
    });
    return sortedResults;
  }, [activeJobs, technicians, selectedStores, liveMapFilter]);

  const jobsFromSessions = useMemo(() => {
    return (sessions || [])
      .filter((session) => session?.current_task_id && session?.tasks)
      .map((session) => session.tasks.find((t) => t.task_id === session.current_task_id)?.task)
      .filter((task) => task?.phase && [JobPhase.Live, JobPhase.Arrived].indexOf(task.phase) > -1);
  }, [sessions]);

  const invitesData = useMemo(() => {
    return invites?.length > 0 ? invites?.map((i) => i?.getInvite()) : [];
  }, [invites]);

  const filteredSessions = useMemo(() => {
    return sessions?.length > 0 ? sessions?.filter((s) => s?.current_task_id) : [];
  }, [sessions]);

  return selectedOrg && (
    <>
      <CRow className="live-map">
          <CCol md={9}>
            <ActiveJobsMap
              key={selectedOrg._id} 
              config={baseLocation ? {
                area: {
                  centerLat: baseLocation?.lat || 0,
                  centerLng: baseLocation?.lng || 0,
                  allowMarkers: true,
                  markers: [{
                    lat: baseLocation?.lat,
                    lng: baseLocation?.lng,
                    ext: {
                      w: config?.theme?.orgIcon?.w || 44,
                      h: config?.theme?.orgIcon?.h || 44,
                      path: config?.theme?.orgIcon?.url || orgLocationIcon,
                    }
                  }]
                }
              } : {}}
              activeTasks={isSessionsEnabled ? jobsFromSessions : filteredJobs} 
              selectedJob={selectedJob} 
              onSelectJob={(inviteCode) => {
                const tasks = isSessionsEnabled ? jobsFromSessions : filteredJobs;
                const job = tasks?.find((task) => task.invite_code === inviteCode);
                if (job) selectJobHandler(job);
              }}
              showDestOnSelect 
              onInviteListChanged={onInviteListChanged} 
            />
          </CCol>
          <CCol md={3}>
            {
              storeOpts?.length > 0 && (
                <MultiSelect
                  options={storeOpts}
                  value={selectedStores}
                  hasSelectAll
                  onChange={onSelectStore}
                  labelledBy="Select Store"
                />
              )
            }
            <CListGroup>
              {
                isSessionsEnabled ? (
                  <LiveMapActiveSessionDrivers sessions={filteredSessions} onSelect={selectJobHandler} selectedJob={selectedJob} invitesData={invitesData} getJobAgentName={getJobAgentName} />
                ) : (
                  <LiveMapActiveDrivers jobs={filteredJobs} onSelect={selectJobHandler} selectedJob={selectedJob} invitesData={invitesData} getJobAgentName={getJobAgentName} />
                )
              }
            </CListGroup>
          </CCol>
        </CRow>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  jobs: selectJobs(state),
  activeJobs: selectLiveMapActiveJobs(state),
  sessions: selectSessions(state),
  sessionsLoading: state?.sessions?.isLoading,
  jobsLoading: state?.jobs?.jobs?.isLoading,
  technicians: selectTechniciansIncludingRemoved(state),
  techniciansLoading: state?.userManagement?.users?.isLoading,
});

export default connect(mapStateToProps)(DashboardLiveMap);